import { useState, useEffect } from "react";
import { useBusiness } from "../context/BusinessContext";
import TopBar from "../components/TopBar";
import ProfileHeader from "../components/ProfileHeader";
import SocialRow from "../components/SocialRow";
import LinkPill from "../components/LinkPill";
import Toast from "../components/Toast";
import ShareModal from "../components/ShareModal";
import LinkPreviewModal from "../components/LinkPreviewModal";
import defaultLogo from "../assets/default-logo.png";
import "../styles/profile.css";

const ProfilePage = () => {
  const { business, loading, error, notFound } = useBusiness();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [showLinkPreviewModal, setShowLinkPreviewModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [shakeButton, setShakeButton] = useState(false);

  // Get background class name based on config
  const getBackgroundClass = () => {
    if (!business?.linktreeConfig?.background) return "profile-page--solid";
    const bg = business.linktreeConfig.background;
    if (bg.type === "gradient") return "profile-page--gradient";
    if (bg.type === "image" && bg.imageUrl) return "profile-page--image";
    return "profile-page--solid";
  };

  // Get inline styles for the profile page element
  const getPageStyles = () => {
    const styles = {};

    // Always use business.color as background if available
    if (business?.color) {
      styles["--bg"] = business.color;
    }

    if (!business?.linktreeConfig) {
      return styles;
    }

    const config = business.linktreeConfig;

    // Set background color from config (overrides business.color if config has it)
    const bg = config.background;
    if (bg) {
      if (bg.type === "solid") {
        styles["--bg"] = bg.solidColor || business.color || "#F28C28";
      } else if (bg.type === "gradient") {
        styles["--gradient-from"] = bg.gradient.from;
        styles["--gradient-to"] = bg.gradient.to;
        styles["--gradient-angle"] = `${bg.gradient.angle || 180}deg`;
      } else if (bg.type === "image" && bg.imageUrl) {
        styles["--bg-image"] = bg.imageUrl;
      }
    }

    // Set other CSS variables
    styles["--text"] = config.textColor || "#FFFFFF";

    const pill = config.pill || {};
    styles["--pill-bg"] = pill.bgColor || "#F7F0E6";
    styles["--pill-text"] = pill.textColor || "#1A1A1A";
    styles["--pill-border"] = pill.borderColor || "#111111";
    styles["--pill-border-width"] = `${pill.borderWidth || 2}px`;
    styles["--pill-radius"] = `${pill.radius || 18}px`;
    styles["--pill-shadow"] = pill.shadow
      ? "0 2px 8px rgba(0, 0, 0, 0.1)"
      : "none";
    styles["--social-icon-color"] = config.socialIconColor || "#FFFFFF";
    styles["--max-width"] = `${config.maxWidth || 420}px`;

    return styles;
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleLinkDotsClick = (link) => {
    setSelectedLink(link);
    setShowLinkPreviewModal(true);
  };

  // Update document title, favicon, and meta tags based on business
  useEffect(() => {
    if (business) {
      const businessName = business.name || business.urlSlug || "Mennu.mx";
      document.title = `Link in bio de ${businessName}`;

      // Update favicon with business logo
      if (business.logo) {
        // Remove existing favicon links
        const existingFavicons = document.querySelectorAll(
          'link[rel="icon"], link[rel="shortcut icon"]'
        );
        existingFavicons.forEach((link) => link.remove());

        // Create new favicon link
        const link = document.createElement("link");
        link.rel = "icon";
        link.type = "image/png";
        link.href = business.logo;
        document.head.appendChild(link);

        // Also update apple-touch-icon
        const existingAppleIcon = document.querySelector(
          'link[rel="apple-touch-icon"]'
        );
        if (existingAppleIcon) {
          existingAppleIcon.href = business.logo;
        } else {
          const appleLink = document.createElement("link");
          appleLink.rel = "apple-touch-icon";
          appleLink.href = business.logo;
          document.head.appendChild(appleLink);
        }

        // Update or create Open Graph meta tags
        const updateOrCreateMeta = (property, content) => {
          let meta = document.querySelector(`meta[property="${property}"]`);
          if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute("property", property);
            document.head.appendChild(meta);
          }
          meta.setAttribute("content", content);
        };

        // Helper to update or create meta tags by name
        const updateOrCreateMetaByName = (name, content) => {
          let meta = document.querySelector(`meta[name="${name}"]`);
          if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute("name", name);
            document.head.appendChild(meta);
          }
          meta.setAttribute("content", content);
        };

        // Get business description from bio or use default
        const businessDescription = business?.linktreeConfig?.bio || 
          `Visita el Link in Bio de ${businessName} y descubre todos sus enlaces importantes en un solo lugar.`;

        // Get current page URL
        const currentUrl = window.location.href;

        // Update Open Graph meta tags
        updateOrCreateMeta("og:image", business.logo);
        updateOrCreateMeta("og:title", `Link in bio de ${businessName}`);
        updateOrCreateMeta("og:description", businessDescription);
        updateOrCreateMeta("og:url", currentUrl);
        updateOrCreateMeta("og:type", "website");

        // Update Twitter Card meta tags
        updateOrCreateMetaByName("twitter:card", "summary_large_image");
        updateOrCreateMetaByName("twitter:image", business.logo);
        updateOrCreateMetaByName("twitter:title", `Link in bio de ${businessName}`);
        updateOrCreateMetaByName("twitter:description", businessDescription);
      }
    } else {
      // Reset to default when business not found
      document.title = "Mennu.mx";
      
      // Set favicon to default logo
      const existingFavicons = document.querySelectorAll(
        'link[rel="icon"], link[rel="shortcut icon"]'
      );
      existingFavicons.forEach((link) => link.remove());

      const link = document.createElement("link");
      link.rel = "icon";
      link.type = "image/png";
      link.href = defaultLogo;
      document.head.appendChild(link);

      // Also update apple-touch-icon
      const existingAppleIcon = document.querySelector(
        'link[rel="apple-touch-icon"]'
      );
      if (existingAppleIcon) {
        existingAppleIcon.href = defaultLogo;
      } else {
        const appleLink = document.createElement("link");
        appleLink.rel = "apple-touch-icon";
        appleLink.href = defaultLogo;
        document.head.appendChild(appleLink);
      }
    }
  }, [business, notFound]);

  // Shake button effect every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setShakeButton(true);
      setTimeout(() => {
        setShakeButton(false);
      }, 500); // Animation duration
    }, 5000); // Repeat every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const sortedLinks =
    business?.links
      ?.filter((link) => link.enabled)
      .sort((a, b) => (a.order || 0) - (b.order || 0)) || [];

  if (loading) {
    return (
      <div
        className="profile-page profile-page--solid"
        style={{ justifyContent: "center", alignItems: "center" }}
      >
        <div className="loading-container">
          <div className="loading-logo-wrapper">
            <img 
              src={defaultLogo} 
              alt="Mennu.mx" 
              className="loading-logo"
            />
          </div>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (notFound || error || !business) {
    return (
      <div className="profile-not-found">
        <div className="not-found-logo-wrapper">
          <img 
            src={defaultLogo} 
            alt="Mennu.mx" 
            className="not-found-logo"
          />
        </div>
        <h1 className="not-found-title">La página que buscas no existe.</h1>
        <p className="not-found-message">
          ¿Quieres que este sea tu usuario?{" "}
          <a
            href="https://mennu.mx"
            target="_blank"
            rel="noopener noreferrer"
            className="not-found-link"
          >
            Crea tu Mennu.mx ahora.
          </a>
        </p>
      </div>
    );
  }

  const config = business.linktreeConfig || {};
  const backgroundClass = getBackgroundClass();

  // Get inline background style for image type
  const getBackgroundStyle = () => {
    if (!business?.linktreeConfig?.background) return {};
    const bg = business.linktreeConfig.background;
    if (bg.type === "image" && bg.imageUrl) {
      return {
        backgroundImage: `url(${bg.imageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    }
    return {};
  };

  const pageStyles = getPageStyles();
  const backgroundStyle = getBackgroundStyle();

  return (
    <div
      className={`profile-page ${backgroundClass}`}
      style={{ ...pageStyles, ...backgroundStyle }}
    >
      <div className="profile-container">
        <TopBar
          showTopBar={config.showTopBar !== false}
          showShare={config.showShare !== false}
          onShare={handleShare}
        />

        <ProfileHeader
          logo={business.logo}
          title={business.name}
          bio={config.bio}
        />

        {config.socialEnabled !== false && (
          <SocialRow
            socialMediaLinks={business.socialMediaLinks}
            iconColor={config.socialIconColor}
          />
        )}

        <div className="links-container">
          {sortedLinks.map((link) => (
            <LinkPill
              key={link._id}
              link={link}
              pillConfig={config.pill || {}}
              onDotsClick={handleLinkDotsClick}
            />
          ))}
        </div>

        {business.brandFooter && (
          <div className="profile-footer">Desarrollado por Mennu.mx</div>
        )}

        <div className="profile-join-button-container">
          <a
            href="https://mennu.mx"
            target="_blank"
            rel="noopener noreferrer"
            className={`profile-join-button ${shakeButton ? 'shake' : ''}`}
          >
            <img
              src={defaultLogo}
              alt="Mennu.mx"
              className="profile-join-button-logo"
            />
            <span className="profile-join-button-text">
              Haz tu Link in Bio gratis
            </span>
          </a>
        </div>
      </div>

      {showToast && (
        <Toast message={toastMessage} onClose={() => setShowToast(false)} />
      )}

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        business={business}
        url={window.location.href}
      />

      <LinkPreviewModal
        isOpen={showLinkPreviewModal}
        onClose={() => {
          setShowLinkPreviewModal(false);
          setSelectedLink(null);
        }}
        link={selectedLink}
        business={business}
        url={selectedLink?.url || ""}
      />
    </div>
  );
};

export default ProfilePage;
